import type { SagaBattle } from './saga.ts';
import { sec } from './units.ts';

/**
 * Brann and Hilde's bets (docs/SAGAS.md, «Las apuestas de Brann y Hilde»): short fights on fields that
 * already exist, with one rule that changes everything. One of the two bets the group can do it, the other
 * that it can't; a win puts a barrel in the bastion. Any order, from their level, as often as wanted; they
 * don't move affinity. Texts in i18n under `bet.<id>.*`.
 */

export type BetId = 'towers' | 'dark' | 'barrel' | 'hundred' | 'unscathed' | 'alone' | 'beside' | 'race' | 'gate';

export interface BetDef {
  id: BetId;
  /** Opens with the group at this level (the active heroes' average). */
  minGroup: number;
  /** Who bets the group can do it (the other one pays the barrel); 'both' for the open gate, which pays a plaque instead. */
  favor: 'brann' | 'hilde' | 'both';
  /** The fight: a saga battle act on an incursion clearing. */
  battle: SagaBattle;
  /** What they say about it, in order (`bet.<id>.<key>`; a few get the last word back). */
  lines: ['brann' | 'hilde', string][];
}

const ARGUE: BetDef['lines'] = [['brann', 'brann'], ['hilde', 'hilde']];

export const BETS: BetDef[] = [
  // No towers: a small hold with the heroes alone (no portable turrets either).
  { id: 'towers', minGroup: 2, favor: 'brann', battle: { kind: 'battle', id: 'towers', mode: 'hold', land: 'quarry', budgetBp: 8000, fourBp: 11000 }, lines: [...ARGUE, ['brann', 'brann2']] },
  // In the dark: every nightmare is hidden but in the heroes' lanterns or the node's brazier.
  { id: 'dark', minGroup: 5, favor: 'brann', battle: { kind: 'battle', id: 'dark', mode: 'hold', land: 'quarry', budgetBp: 7500, inkBp: 1500, fourBp: 11000 }, lines: ARGUE },
  // The barrel: the old garrison's beer, pushed from the barracks to the forge; it leaks when hit.
  { id: 'barrel', minGroup: 8, favor: 'brann', battle: { kind: 'battle', id: 'barrel', mode: 'escort', land: 'quarry', budgetBp: 12500, cartHpBp: 8000, inkBp: 2000, fourBp: 9000 }, lines: ARGUE },
  // A hundred at a blow: goblin packs pour in; a hundred must fall inside ten seconds before the hold runs out.
  { id: 'hundred', minGroup: 12, favor: 'hilde', battle: { kind: 'battle', id: 'hundred', mode: 'hold', land: 'quarry', budgetBp: 10000 }, lines: ARGUE },
  // Not a scratch: Hilde's stone ring close around the node; one blow on it loses the bet.
  { id: 'unscathed', minGroup: 16, favor: 'hilde', battle: { kind: 'battle', id: 'unscathed', mode: 'hold', land: 'quarry', budgetBp: 3500, inkBp: 1000 }, lines: ARGUE },
  // One alone: the whole fight down a single road, held by one hero with no turrets, as Ser Gaspar did.
  { id: 'alone', minGroup: 20, favor: 'brann', battle: { kind: 'battle', id: 'alone', mode: 'hold', land: 'quarry', budgetBp: 3500, inkBp: 1500 }, lines: ARGUE },
  // Beside: Hilde's towers round the node, in pairs; a tower shoots only with a hero standing by it, no turrets.
  { id: 'beside', minGroup: 25, favor: 'hilde', battle: { kind: 'battle', id: 'beside', mode: 'hold', land: 'quarry', budgetBp: 8000, inkBp: 2000, fourBp: 11000 }, lines: ARGUE },
  // The race: down the deep crypt's halls to its Warden against Brann's old time (BET.race).
  {
    id: 'race',
    minGroup: 32,
    favor: 'hilde',
    battle: { kind: 'battle', id: 'race', mode: 'descent', rooms: 4, budgetBp: 22000, inkBp: 5000, fourBp: 9500, bestiary: { skeleton: 3, wraith: 2, gargoyle: 2, troll: 2, stalker: 2 }, boss: { kind: 'warden', tier: 2, hp: [0, 3200, 4800, 10500, 20000] } },
    lines: ARGUE,
  },
  // The open gate: Brann's wall round the node with one gate, open, and a whole wave to hold through it (BET.gate).
  { id: 'gate', minGroup: 40, favor: 'both', battle: { kind: 'battle', id: 'gate', mode: 'hold', land: 'quarry', budgetBp: 12000, inkBp: 3500, fourBp: 11000 }, lines: [...ARGUE, ['brann', 'brann2']] },
];

export const BET_IDS: BetId[] = BETS.map((b) => b.id);

export const BET = {
  /** XP for each hero in a won bet (the first time; later wins pay half). */
  xp: 400,
  /** In the dark: how far a standing hero's lantern shows the nightmares (fixed units; the node's brazier, KEEP_LIGHT). */
  lantern: 2600,
  /** A hundred at a blow: this many kills inside this window wins; never more than `alive` on the field at once. */
  burst: { count: 100, ticks: sec(10), alive: 115 },
  /** …from goblin packs this big, one every this long, from every road at once. */
  packs: { size: 34, every: sec(9), first: sec(4), tierBp: 1500 },
  /** …and the hold lasts this long (if the hundred never fall together, it is lost); the node holds this much more. */
  hundredTicks: sec(150),
  nodeBp: 1000000,
  /** Not a scratch: Hilde's ring, this far around the node, this level (stone, `material` 2); the first nightmares come this much later. */
  ring: { radius: 2, level: 2 as const, material: 2 as const, lead: sec(10) },
  /** One alone: this many nightmares past the road's mouth lose it; the first come this much later. */
  alone: { max: 4, lead: sec(8) },
  /** Beside: Hilde's towers, `pairs` pairs of them (one of each `types`) spread round ring `ring`, at this level and stone; one shoots only with a standing hero within `reach`. */
  /**
   * The race: the crypt's nightmares and Warden as tough as the group (`toughBp`, as a saga chapter's), and the Warden
   * felled before `limit` from the start; `brann` is the time he says he did it in (the HUD shows it, nothing else).
   */
  /**
   * The open gate: a stone wall ring `radius` round the node (level, `material`), with its one gate open facing one of
   * the roads (by seed); nothing can close it. The first nightmares come `lead` later. Won: Hilde forges a plaque
   * for the bastion's gate with the names of the heroes who held it.
   */
  gate: { radius: 3, level: 3 as const, material: 2 as const, lead: sec(8) },
  race: { toughBp: 26000, limit: sec(180), brann: sec(90) },
  beside: { pairs: 3, ring: 2, types: ['ballista', 'arcane'] as const, level: 4 as const, material: 2 as const, reach: 1300 },
};
